import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "card",
    "checkbox",
    "toolbar",
    "selectButton",
    "cancelButton",
    "count",
    "selectAllButton",
    "deselectAllButton",
    "removeButton",
    "destroyForm",
    "formInputs",
    "likeButton"
  ]

  static values = {
    active: Boolean,
    removeConfirmText: { type: String, default: "Are you sure you want to remove %{count} cars from your favorites?" },
  }

  connect() {
    this.selectedIds = new Set()
    this.activeValue = false
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)
    this.updateUI()
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  toggleSelectionMode() {
    this.activeValue = !this.activeValue
    if (!this.activeValue) this.selectedIds.clear()
    this.updateUI()
  }

  handleCheckboxClick(event) {
    event.stopPropagation()
  }

  toggleCard(event) {
    if (!this.activeValue) return
    
    const target = event.target
    const isGalleryArrow = target.closest('[data-gallery-target="arrowLeft"], [data-gallery-target="arrowRight"]')
    if (isGalleryArrow) return
    
    event.preventDefault()
    event.stopPropagation()
    
    const card = event.target.type === 'checkbox' 
      ? this.cardTargets[this.checkboxTargets.indexOf(event.target)]
      : event.currentTarget.closest('[data-multi-select-target="card"]')
    
    if (!card) return
    
    const carId = card.dataset.carId
    if (!carId) return
    
    if (this.selectedIds.has(carId)) {
      this.selectedIds.delete(carId)
    } else {
      this.selectedIds.add(carId)
    }
    
    this.updateUI()
  }

  selectAll() {
    if (!this.activeValue) return
    
    this.cardTargets.forEach(card => {
      const carId = card.dataset.carId
      if (carId) this.selectedIds.add(carId)
    })
    
    this.updateUI()
  }

  deselectAll() {
    this.selectedIds.clear()
    this.updateUI()
  }

  removeSelected() {
    if (this.selectedIds.size === 0) return
    if (!this.hasDestroyFormTarget || !this.hasFormInputsTarget) return
    
    const carIds = Array.from(this.selectedIds)
    const count = carIds.length
    
    if (!confirm(this._interpolate(this.removeConfirmTextValue, { count: count }))) return
    
    this.formInputsTarget.innerHTML = ''
    carIds.forEach(carId => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'car_ids[]'
      input.value = carId
      this.formInputsTarget.appendChild(input)
    })
    
    this.destroyFormTarget.submit()
  }

  handleKeydown(event) {
    if (!this.activeValue) return
    
    if (event.key === 'Escape') {
      event.preventDefault()
      this.toggleSelectionMode()
    } else if (event.key === 'a' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      this.selectAll()
    }
  }

  activeValueChanged() {
    this.updateUI()
  }

  updateUI() {
    this.updateCards()
    this.updateToolbar()
    this.updateModeButtons()
    this.updateSelectionButtons()
    this.updateRemoveButton()
  }

  updateCards() {
    if (!this.selectedIds) return
    
    this.cardTargets.forEach((card, index) => {
      const carId = card.dataset.carId
      if (!carId) return
      
      const isSelected = this.selectedIds.has(carId)
      const checkbox = this.checkboxTargets[index]
      const likeButton = this.likeButtonTargets[index]
      const shouldHighlight = isSelected && this.activeValue
      
      if (checkbox) {
        checkbox.checked = isSelected
        checkbox.classList.toggle('opacity-0', !this.activeValue)
        checkbox.classList.toggle('pointer-events-none', !this.activeValue)
      }
      
      card.querySelectorAll('[data-multi-select-target="cardLink"]').forEach(link => {
        if (this.activeValue) {
          this.disableElement(link, true)
        } else {
          this.enableElement(link, true)
        }
      })
      
      if (likeButton) {
        if (this.activeValue) {
          this.disableElement(likeButton, false)
        } else {
          this.enableElement(likeButton, false)
        }
      }
      
      card.classList.toggle('ring-2', shouldHighlight)
      card.classList.toggle('ring-red-500', shouldHighlight)
      card.classList.toggle('ring-offset-2', shouldHighlight)
      card.classList.toggle('rounded-xl', shouldHighlight)
      card.classList.toggle('scale-[0.98]', shouldHighlight)
      card.classList.toggle('shadow-xl', shouldHighlight)
      card.classList.toggle('bg-red-500/[.05]', shouldHighlight)
      card.classList.toggle('cursor-pointer', this.activeValue)
    })
  }

  updateToolbar() {
    if (!this.selectedIds) return
    
    if (this.hasToolbarTarget) {
      this.toolbarTarget.classList.toggle('hidden', !(this.activeValue && this.selectedIds.size > 0))
    }
  }

  updateModeButtons() {
    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.classList.toggle('hidden', this.activeValue)
    }
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.toggle('hidden', !this.activeValue)
    }
  }

  updateSelectionButtons() {
    if (!this.selectedIds) return
    
    if (this.hasCountTarget) {
      this.countTarget.textContent = this.selectedIds.size
    }
    if (this.hasSelectAllButtonTarget) {
      const allSelected = this.cardTargets.length > 0 && 
        this.cardTargets.every(card => {
          const carId = card.dataset.carId
          return carId && this.selectedIds.has(carId)
        })
      this.selectAllButtonTarget.classList.toggle('hidden', allSelected)
    }
    if (this.hasDeselectAllButtonTarget) {
      this.deselectAllButtonTarget.classList.toggle('hidden', this.selectedIds.size === 0)
    }
  }

  updateRemoveButton() {
    if (!this.selectedIds) return
    
    if (this.hasRemoveButtonTarget) {
      const hasSelection = this.selectedIds.size > 0
      this.removeButtonTarget.disabled = !hasSelection
      this.removeButtonTarget.classList.toggle('opacity-50', !hasSelection)
      this.removeButtonTarget.classList.toggle('cursor-not-allowed', !hasSelection)
    }
  }

  disableElement(element, isLink) {
    element.style.pointerEvents = 'none'
    if (isLink) {
      element.style.cursor = 'default'
    } else {
      element.style.opacity = '0.5'
    }
  }

  enableElement(element, isLink) {
    element.style.pointerEvents = ''
    if (isLink) {
      element.style.cursor = ''
    } else {
      element.style.opacity = ''
    }
  }

  _interpolate(text, params) {
    return text.replace(/%\{(\w+)\}/g, (_, key) => params[key] != null ? params[key] : "")
  }
}
